import { Component, ElementRef, Input, OnChanges, OnInit, SimpleChanges, ViewChild } from '@angular/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import {
  ManageWellspaceProductSelection,
  TeamCompaniesService,
} from '../../../services/teamcompanies/teamcompanies.service';
import { TeamsService } from '../../../services/teams/teams.service';
import { UiAlertService } from '../../../services/ui-alert/ui-alert.service';
import { StripeService } from '../../../services/stripe/stripe.service';
import { VendorProduct } from '../../../services/vendorproducts/vendorproducts.service';
import { SessionService } from '../../../services/session/session.service';

interface ManageWellspaceProduct {
  id?: string;
  vendorProductId?: string;
  productId?: string;
  stripeProductId?: string;
  vendorName?: string;
  productName?: string;
  stripeProduct?: any;
  image?: any;
  imageUrl?: string;
  logoUrl?: string;
  iconUrl?: string;
  avatarUrl?: string;
  lightLogoUrl?: string;
  horizontalLogoUrl?: string;
  metadata?: any;
  loadedImage?: string | null;
  selected?: boolean;
  locked?: boolean;
  allowanceEligible?: boolean;
  allowanceSaving?: boolean;
  allowanceError?: string;
}

interface ManageWellspaceProductSourceTeamOption {
  id: string;
  label: string;
}

@Component({
  selector: 'app-managewellspace-products',
  templateUrl: './managewellspace-products.component.html',
  styleUrls: ['./managewellspace-products.component.scss'],
})
export class ManageWellspaceProductsComponent implements OnInit, OnChanges {
  @Input() teamId: string | undefined;
  @Input() companyId = '';

  protected products: ManageWellspaceProduct[] = [];
  protected team: any;
  protected company: any;
  protected isLoading = false;
  protected isSaving = false;
  protected saveError = '';
  protected savingOperation: 'save' | 'reset' | '' = '';
  protected sourceTeamOptions: ManageWellspaceProductSourceTeamOption[] = [];
  protected selectedSourceTeamId = '';
  protected sourceTeamOptionsError = '';
  protected sourceTeamProductPreviewError = '';
  protected sourceTeamProductPreviewProductIds: string[] = [];
  protected sourceTeamProductPreviewAddedProductIds: string[] = [];
  protected sourceTeamProductPreviewOverlappingProductIds: string[] = [];
  protected sourceTeamProductPreviewRemovedProductIds: string[] = [];
  protected sourceTeamProductPreviewSkippedProductIds: string[] = [];
  protected sourceTeamProductPreviewAllowanceEligibleProductIds: string[] = [];
  protected sourceTeamProductPreviewApplied = false;
  protected isSourceTeamOptionsLoading = false;
  protected isSourceTeamProductPreviewLoading = false;
  protected isSourceTeamProductHelperExpanded = true;
  protected showProductScrollHint = false;

  @ViewChild('productsSection') private productsSection?: ElementRef<HTMLElement>;
  @ViewChild('productList')
  private set productList(productList: ElementRef<HTMLElement> | undefined) {
    if (productList) {
      requestAnimationFrame(() => this.updateProductScrollState());
    }
  }

  private companyProductIdsInOrder: string[] = [];
  private lastSavedSelectedProductIds: string[] = [];
  private lastSavedAllowanceEligibleProductIds: string[] = [];
  private alwaysActiveProductIds = new Set<string>();
  private allowanceControlDisabledProductIds = new Set<string>();
  private activeProductsOverride = false;
  private isInitialized = false;
  private missingProductImageQueue: ManageWellspaceProduct[] = [];
  private missingProductImageActiveRequests = 0;
  private missingProductImageLoadVersion = 0;
  private stripeProductImageRequests = new Map<string, Promise<string>>();
  private readonly missingProductImageMaxConcurrentLoads = 4;

  constructor(
    private teamsService: TeamsService,
    private teamCompaniesService: TeamCompaniesService,
    private uiAlertService: UiAlertService,
    private snackBar: MatSnackBar,
    private stripeService: StripeService,
    private sessionService: SessionService,
  ) {}

  async ngOnInit(): Promise<void> {
    this.isInitialized = true;
    await this.refresh();
  }

  async ngOnChanges(changes: SimpleChanges): Promise<void> {
    if (!this.isInitialized) {
      return;
    }

    if (changes.teamId || changes.companyId) {
      await this.refresh();
    }
  }

  protected get selectedProductCount(): number {
    return this.getSelectedProductIds().length;
  }

  protected hasTeamProductOverride(): boolean {
    return this.activeProductsOverride;
  }

  protected hasProductSelectionChanges(): boolean {
    return !this.areProductIdListsEqual(
      this.getSelectedProductIds(),
      this.lastSavedSelectedProductIds,
    ) || !this.areProductIdListsEqual(
      this.getAllowanceEligibleProductIds(),
      this.lastSavedAllowanceEligibleProductIds,
    );
  }

  protected get isResetting(): boolean {
    return this.isSaving && this.savingOperation === 'reset';
  }

  protected get busyStatusMessage(): string {
    if (this.isResetting) {
      return 'Clearing team override...';
    }

    if (this.isSaving) {
      return 'Saving products...';
    }

    return '';
  }

  protected updateProductScrollState(): void {
    const section = this.productsSection?.nativeElement;
    if (!section) {
      this.showProductScrollHint = false;
      return;
    }

    const hasOverflow = section.scrollHeight > section.clientHeight + 1;
    const isAtBottom = section.scrollTop + section.clientHeight >= section.scrollHeight - 1;
    this.showProductScrollHint = hasOverflow && !isAtBottom;
  }

  protected isProductLocked(product: ManageWellspaceProduct): boolean {
    const productId = this.getProductKey(product);
    return !!productId && this.alwaysActiveProductIds.has(productId);
  }

  protected toggleProductSelection(product: ManageWellspaceProduct, selected: boolean): void {
    if (this.isProductLocked(product)) {
      product.selected = true;
      return;
    }

    product.selected = selected;
    this.saveError = '';
    this.sourceTeamProductPreviewApplied = false;
  }

  protected get isProductBulkSelectionDisabled(): boolean {
    return this.isLoading || this.isSaving || this.getSelectableProducts().length === 0;
  }

  protected areAllSelectableProductsSelected(): boolean {
    const selectableProducts = this.getSelectableProducts();
    return selectableProducts.length > 0 && selectableProducts.every((product) => product.selected === true);
  }

  protected areSomeSelectableProductsSelected(): boolean {
    const selectableProducts = this.getSelectableProducts();
    const selectedProductCount = selectableProducts.filter((product) => product.selected === true).length;
    return selectedProductCount > 0 && selectedProductCount < selectableProducts.length;
  }

  protected toggleAllProductSelections(selected: boolean): void {
    if (this.isProductBulkSelectionDisabled) {
      return;
    }

    this.getSelectableProducts().forEach((product) => {
      product.selected = selected;
    });
    this.saveError = '';
    this.sourceTeamProductPreviewApplied = false;
  }

  protected canManageAllowanceEligibility(product: ManageWellspaceProduct): boolean {
    const productId = this.getProductKey(product);
    return !!this.teamId
      && !!productId
      && !this.allowanceControlDisabledProductIds.has(productId)
      && (product.selected === true || this.isProductLocked(product));
  }

  protected canToggleAllowanceEligibility(product: ManageWellspaceProduct): boolean {
    return this.canManageAllowanceEligibility(product);
  }

  protected toggleAllowanceEligibility(product: ManageWellspaceProduct, allowanceEligible: boolean): void {
    if (!this.teamId || !this.canToggleAllowanceEligibility(product)) {
      return;
    }

    product.allowanceEligible = allowanceEligible;
    product.allowanceError = '';
    this.saveError = '';
    this.sourceTeamProductPreviewApplied = false;
  }

  protected get isAllowanceBulkSelectionDisabled(): boolean {
    return this.isLoading || this.isSaving || this.getAllowanceEligibilityTargets().length === 0;
  }

  protected areAllAllowanceTargetsEligible(): boolean {
    const targets = this.getAllowanceEligibilityTargets();
    return targets.length > 0 && targets.every((product) => product.allowanceEligible === true);
  }

  protected areSomeAllowanceTargetsEligible(): boolean {
    const targets = this.getAllowanceEligibilityTargets();
    const eligibleProductCount = targets.filter((product) => product.allowanceEligible === true).length;
    return eligibleProductCount > 0 && eligibleProductCount < targets.length;
  }

  protected toggleAllAllowanceEligibility(allowanceEligible: boolean): void {
    if (this.isAllowanceBulkSelectionDisabled) {
      return;
    }

    this.getAllowanceEligibilityTargets().forEach((product) => {
      product.allowanceEligible = allowanceEligible;
      product.allowanceError = '';
    });
    this.saveError = '';
    this.sourceTeamProductPreviewApplied = false;
  }

  protected toggleSourceTeamProductHelper(): void {
    this.isSourceTeamProductHelperExpanded = !this.isSourceTeamProductHelperExpanded;
  }

  protected async loadSourceTeamProductPreview(): Promise<void> {
    this.resetSourceTeamProductPreview();

    if (!this.selectedSourceTeamId) {
      this.sourceTeamProductPreviewError = 'Choose a team first.';
      return;
    }

    if (this.selectedSourceTeamId === this.teamId) {
      this.sourceTeamProductPreviewError = 'Choose a different team.';
      return;
    }

    this.isSourceTeamProductPreviewLoading = true;
    try {
      const selection = await this.teamsService.getTeamProductSelection(this.selectedSourceTeamId);
      this.buildSourceTeamProductPreview(selection);
    } catch (error) {
      this.sourceTeamProductPreviewError = this.getErrorMessage(error);
    } finally {
      this.isSourceTeamProductPreviewLoading = false;
    }
  }

  protected canApplySourceTeamProductPreview(): boolean {
    return this.sourceTeamProductPreviewProductIds.length > 0 && !this.isSourceTeamProductPreviewLoading;
  }

  protected applySourceTeamProductPreview(): void {
    if (!this.canApplySourceTeamProductPreview()) {
      return;
    }

    const nextProductIds = new Set(this.sourceTeamProductPreviewProductIds);
    const nextAllowanceEligibleProductIds = new Set(this.sourceTeamProductPreviewAllowanceEligibleProductIds);
    this.products = this.products.map((product) => {
      const productId = this.getProductKey(product);
      const locked = this.isProductLocked(product);
      return {
        ...product,
        selected: locked || nextProductIds.has(productId),
        allowanceEligible: this.allowanceControlDisabledProductIds.has(productId)
          ? product.allowanceEligible
          : nextAllowanceEligibleProductIds.has(productId),
        allowanceError: '',
      };
    });
    this.saveError = '';
    this.sourceTeamProductPreviewApplied = true;
  }

  protected getProductDisplay(productId: string): string {
    const product = this.products.find((candidate) => this.getProductKey(candidate) === productId);
    if (!product) {
      return productId;
    }

    return [product.vendorName, product.productName]
      .map((value) => `${value || ''}`.trim())
      .filter(Boolean)
      .join(' - ') || productId;
  }

  protected async saveProducts(): Promise<void> {
    if (this.isSaving || !this.companyId) {
      return;
    }

    this.isSaving = true;
    this.savingOperation = 'save';
    this.saveError = '';
    try {
      const productIds = this.getSelectedProductIds();
      const allowanceEligibleProductIds = this.getAllowanceEligibleProductIds();
      let selection = this.teamId
        ? await this.saveTeamProductsAndAllowanceEligibility(productIds, allowanceEligibleProductIds)
        : await this.teamCompaniesService.saveCompanyProductSelection(this.companyId, productIds);
      await this.applySelection(selection);
      this.snackBar.open('Products saved.', 'Dismiss', { duration: 2500 });
    } catch (error) {
      this.saveError = this.getErrorMessage(error);
    } finally {
      this.isSaving = false;
      this.savingOperation = '';
    }
  }

  protected async cancelProductChanges(): Promise<void> {
    await this.refresh();
  }

  protected async clearTeamProductOverride(): Promise<void> {
    if (!this.teamId || this.isSaving) {
      return;
    }

    const confirm = await this.uiAlertService.presentAlertConfirm(
      'Clear this team\'s product override? The team will use company-level product settings. Always Active products will remain selected here.',
    );
    if (!confirm) {
      return;
    }

    this.isSaving = true;
    this.savingOperation = 'reset';
    this.saveError = '';
    try {
      const selection = await this.teamsService.resetTeamProductSelection(this.teamId);
      await this.applySelection(selection);
      this.snackBar.open('Team product override cleared.', 'Dismiss', { duration: 2500 });
    } catch (error) {
      this.saveError = this.getErrorMessage(error);
    } finally {
      this.isSaving = false;
      this.savingOperation = '';
    }
  }

  protected trackByProductId = (_: number, product: ManageWellspaceProduct): string => {
    return this.getProductKey(product);
  };

  private async refresh(): Promise<void> {
    if (!this.companyId && !this.teamId) {
      this.products = [];
      this.companyProductIdsInOrder = [];
      this.lastSavedSelectedProductIds = [];
      this.lastSavedAllowanceEligibleProductIds = [];
      this.alwaysActiveProductIds = new Set<string>();
      this.activeProductsOverride = false;
      this.cancelMissingProductImageLoads();
      return;
    }

    this.isLoading = true;
    this.saveError = '';
    try {
      const selection = this.teamId
        ? await this.teamsService.getTeamProductSelection(this.teamId)
        : await this.teamCompaniesService.getCompanyProductSelection(this.companyId);
      await this.applySelection(selection);
      void this.loadProductSourceTeams();
    } catch (error) {
      this.products = [];
      this.cancelMissingProductImageLoads();
      this.saveError = this.getErrorMessage(error);
    } finally {
      this.isLoading = false;
    }
  }

  private async applySelection(selection: ManageWellspaceProductSelection): Promise<void> {
    this.company = {
      id: selection.companyId,
      name: selection.companyName,
    };
    this.team = selection.teamId
      ? {
        id: selection.teamId,
        name: selection.teamName,
      }
      : undefined;
    this.companyId = selection.companyId || this.companyId;
    this.companyProductIdsInOrder = Array.isArray(selection.availableProductIds)
      ? selection.availableProductIds
      : [];
    this.alwaysActiveProductIds = new Set(selection.alwaysActiveProductIds || []);
    this.allowanceControlDisabledProductIds = new Set(selection.allowanceControlDisabledProductIds || []);
    this.activeProductsOverride = selection.activeProductsOverride === true;
    this.lastSavedSelectedProductIds = this.normalizeProductIdsInCompanyOrder(selection.selectedProductIds || []);
    this.lastSavedAllowanceEligibleProductIds = this.normalizeProductIdsInAvailableOrder(
      selection.allowanceEligibleProductIds || [],
    );

    const selectedSet = new Set(this.lastSavedSelectedProductIds);
    const vendorProducts = this.getProductsInCompanyOrder(selection);
    const allowanceEligibleSet = new Set(selection.allowanceEligibleProductIds || []);
    const products = vendorProducts.map((vendorProduct) => {
      const productId = this.getProductKey(vendorProduct);
      const locked = this.alwaysActiveProductIds.has(productId);
      return {
        ...vendorProduct,
        selected: locked || selectedSet.has(productId),
        locked,
        allowanceEligible: allowanceEligibleSet.has(productId),
        allowanceSaving: false,
        allowanceError: '',
        loadedImage: this.resolveProductImageUrl(vendorProduct),
      };
    });

    this.products = products;
    this.loadMissingProductImages();
    this.resetSourceTeamProductPreview();
  }

  private async loadProductSourceTeams(): Promise<void> {
    this.sourceTeamOptions = [];
    this.selectedSourceTeamId = '';
    this.sourceTeamOptionsError = '';
    this.isSourceTeamOptionsLoading = false;
    this.resetSourceTeamProductPreview();

    if (!this.teamId) {
      return;
    }

    this.isSourceTeamOptionsLoading = true;
    try {
      const teams = await this.getAvailableProductSourceTeams();
      this.sourceTeamOptions = this.normalizeSourceTeamOptions(teams);
      this.selectedSourceTeamId = this.sourceTeamOptions[0]?.id || '';
    } catch (error) {
      this.sourceTeamOptionsError = this.getErrorMessage(error);
    } finally {
      this.isSourceTeamOptionsLoading = false;
    }
  }

  private async getAvailableProductSourceTeams(): Promise<any[]> {
    if (this.companyId) {
      try {
        return await this.teamsService.getTeamsForCompany(this.companyId);
      } catch (error) {
        if (this.sessionService.isSuperAdmin()) {
          throw error;
        }
      }
    }

    const userId = this.sessionService.getUserId();
    return userId
      ? this.teamsService.getUserTeamsInfo(userId)
      : [];
  }

  private normalizeSourceTeamOptions(teams: any[]): ManageWellspaceProductSourceTeamOption[] {
    const seenIds = new Set<string>();
    return (Array.isArray(teams) ? teams : [])
      .reduce((options: ManageWellspaceProductSourceTeamOption[], team) => {
        const id = `${team?.id || ''}`.trim();
        if (!id || id === this.teamId || seenIds.has(id)) {
          return options;
        }

        seenIds.add(id);
        options.push({
          id,
          label: `${team?.name || id}`.trim(),
        });
        return options;
      }, [])
      .sort((left, right) => left.label.localeCompare(right.label, undefined, {
        numeric: true,
        sensitivity: 'base',
      }));
  }

  private buildSourceTeamProductPreview(selection: ManageWellspaceProductSelection): void {
    const sourceProductIds = this.getUniqueProductIds(selection?.selectedProductIds || []);
    const currentAvailableProductIds = new Set(this.companyProductIdsInOrder);
    const matchedProductIds = sourceProductIds.filter((productId) => currentAvailableProductIds.has(productId));
    const skippedProductIds = sourceProductIds.filter((productId) => !currentAvailableProductIds.has(productId));
    const nextEffectiveProductIds = new Set(matchedProductIds);
    this.alwaysActiveProductIds.forEach((productId) => nextEffectiveProductIds.add(productId));

    const currentProductIds = new Set(this.getSelectedProductIds());
    this.sourceTeamProductPreviewProductIds = this.normalizeProductIdsInAvailableOrder(matchedProductIds);
    this.sourceTeamProductPreviewAddedProductIds = this.normalizeProductIdsInAvailableOrder(
      Array.from(nextEffectiveProductIds).filter((productId) => !currentProductIds.has(productId))
    );
    this.sourceTeamProductPreviewOverlappingProductIds = this.normalizeProductIdsInAvailableOrder(
      Array.from(nextEffectiveProductIds).filter((productId) => currentProductIds.has(productId))
    );
    this.sourceTeamProductPreviewRemovedProductIds = this.normalizeProductIdsInAvailableOrder(
      Array.from(currentProductIds).filter((productId) => !nextEffectiveProductIds.has(productId))
    );
    this.sourceTeamProductPreviewSkippedProductIds = skippedProductIds;
    this.sourceTeamProductPreviewAllowanceEligibleProductIds = this.normalizeProductIdsInAvailableOrder(
      this.getUniqueProductIds(selection?.allowanceEligibleProductIds || [])
        .filter((productId) => nextEffectiveProductIds.has(productId))
        .filter((productId) => !this.allowanceControlDisabledProductIds.has(productId))
    );
  }

  private resetSourceTeamProductPreview(): void {
    this.sourceTeamProductPreviewError = '';
    this.sourceTeamProductPreviewProductIds = [];
    this.sourceTeamProductPreviewAddedProductIds = [];
    this.sourceTeamProductPreviewOverlappingProductIds = [];
    this.sourceTeamProductPreviewRemovedProductIds = [];
    this.sourceTeamProductPreviewSkippedProductIds = [];
    this.sourceTeamProductPreviewAllowanceEligibleProductIds = [];
    this.sourceTeamProductPreviewApplied = false;
  }

  private getProductsInCompanyOrder(selection: ManageWellspaceProductSelection): ManageWellspaceProduct[] {
    const productDetailsById = new Map<string, VendorProduct>();
    (selection.products || []).forEach((product) => {
      const productId = this.getProductKey(product);
      if (productId) {
        productDetailsById.set(productId, product);
      }
    });

    return this.companyProductIdsInOrder.map((productId) => {
      return productDetailsById.get(productId) || {
        id: productId,
        vendorProductId: productId,
        productName: productId,
        metadata: {},
        createdAt: '',
        modifiedAt: '',
      };
    }).sort((left, right) =>
      this.getProductSortLabel(left).localeCompare(this.getProductSortLabel(right), undefined, {
        numeric: true,
        sensitivity: 'base',
      })
    );
  }

  private getProductSortLabel(product: ManageWellspaceProduct): string {
    return [
      product.vendorName,
      product.productName,
      this.getProductKey(product),
    ].map((value) => `${value || ''}`.trim()).filter(Boolean).join(' ');
  }

  private getSelectedProductIds(): string[] {
    const selectedProductIds = this.products
      .filter((product) => product.selected || this.isProductLocked(product))
      .map((product) => this.getProductKey(product))
      .filter(Boolean);

    return this.normalizeProductIdsInCompanyOrder(selectedProductIds);
  }

  private getSelectableProducts(): ManageWellspaceProduct[] {
    return this.products.filter((product) => !this.isProductLocked(product));
  }

  private getAllowanceEligibilityTargets(): ManageWellspaceProduct[] {
    return this.products.filter((product) => this.canManageAllowanceEligibility(product));
  }

  private getAllowanceEligibleProductIds(): string[] {
    const allowanceEligibleProductIds = this.products
      .filter((product) => product.allowanceEligible === true)
      .map((product) => this.getProductKey(product))
      .filter(Boolean);

    return this.normalizeProductIdsInAvailableOrder(allowanceEligibleProductIds);
  }

  private normalizeProductIdsInCompanyOrder(productIds: string[]): string[] {
    const selectedSet = new Set((productIds || []).map((productId) => `${productId || ''}`.trim()).filter(Boolean));
    this.alwaysActiveProductIds.forEach((productId) => selectedSet.add(productId));
    return this.companyProductIdsInOrder.filter((productId) => selectedSet.has(productId));
  }

  private normalizeProductIdsInAvailableOrder(productIds: string[]): string[] {
    const selectedSet = new Set((productIds || []).map((productId) => `${productId || ''}`.trim()).filter(Boolean));
    return this.companyProductIdsInOrder.filter((productId) => selectedSet.has(productId));
  }

  private getUniqueProductIds(productIds: string[]): string[] {
    const seenIds = new Set<string>();
    return (productIds || [])
      .map((productId) => `${productId || ''}`.trim())
      .filter((productId) => {
        if (!productId || seenIds.has(productId)) {
          return false;
        }
        seenIds.add(productId);
        return true;
      });
  }

  private async saveTeamProductsAndAllowanceEligibility(
    productIds: string[],
    allowanceEligibleProductIds: string[],
  ): Promise<ManageWellspaceProductSelection> {
    if (!this.teamId) {
      throw new Error('teamId is required');
    }

    const productSelectionChanged = !this.areProductIdListsEqual(productIds, this.lastSavedSelectedProductIds);
    return this.teamsService.saveTeamProductSelection(
      this.teamId,
      productSelectionChanged ? productIds : undefined,
      allowanceEligibleProductIds,
    );
  }

  private areProductIdListsEqual(left: string[], right: string[]): boolean {
    if (left.length !== right.length) {
      return false;
    }

    return left.every((productId, index) => productId === right[index]);
  }

  private getProductKey(vendorProduct: any): string {
    return (
      vendorProduct?.id ??
      vendorProduct?.vendorProductId ??
      vendorProduct?.productId ??
      vendorProduct?.stripeProductId ??
      ''
    ).toString();
  }

  private getErrorMessage(error: any): string {
    return error?.data?.error
      || error?.error?.error
      || error?.message
      || 'Unable to update products.';
  }

  private cancelMissingProductImageLoads(): void {
    this.missingProductImageLoadVersion += 1;
    this.missingProductImageQueue = [];
    this.missingProductImageActiveRequests = 0;
  }

  private loadMissingProductImages(): void {
    this.missingProductImageLoadVersion += 1;
    this.missingProductImageActiveRequests = 0;
    this.missingProductImageQueue = this.products.filter((product) =>
      !this.getTrimmedText(product?.loadedImage) && !!this.getTrimmedText(product?.stripeProductId)
    );
    this.fillMissingProductImageQueue(this.missingProductImageLoadVersion);
  }

  private fillMissingProductImageQueue(loadVersion: number): void {
    while (
      loadVersion === this.missingProductImageLoadVersion
      && this.missingProductImageActiveRequests < this.missingProductImageMaxConcurrentLoads
      && this.missingProductImageQueue.length > 0
    ) {
      const product = this.missingProductImageQueue.shift();
      if (!product) {
        continue;
      }

      this.missingProductImageActiveRequests += 1;
      void this.loadMissingProductImage(product, loadVersion)
        .finally(() => {
          if (loadVersion !== this.missingProductImageLoadVersion) {
            return;
          }
          this.missingProductImageActiveRequests = Math.max(0, this.missingProductImageActiveRequests - 1);
          this.fillMissingProductImageQueue(loadVersion);
        });
    }
  }

  private async loadMissingProductImage(product: ManageWellspaceProduct, loadVersion: number): Promise<void> {
    const productId = this.getProductKey(product);
    const stripeProductId = this.getTrimmedText(product?.stripeProductId);
    if (!productId || !stripeProductId || loadVersion !== this.missingProductImageLoadVersion) {
      return;
    }

    const imageUrl = await this.getStripeProductImageUrl(stripeProductId).catch(() => '');
    if (!imageUrl || loadVersion !== this.missingProductImageLoadVersion) {
      return;
    }

    this.products = this.products.map((candidate) => {
      if (this.getProductKey(candidate) !== productId || this.getTrimmedText(candidate.loadedImage)) {
        return candidate;
      }
      return {
        ...candidate,
        loadedImage: imageUrl,
      };
    });
  }

  private getStripeProductImageUrl(stripeProductId: string): Promise<string> {
    const normalizedStripeProductId = this.getTrimmedText(stripeProductId);
    const existingRequest = this.stripeProductImageRequests.get(normalizedStripeProductId);
    if (existingRequest) {
      return existingRequest;
    }

    const request = this.stripeService.getProduct(normalizedStripeProductId)
      .then((stripeProduct: any) => this.getImageUrlCandidates(stripeProduct?.images)[0] || '')
      .catch(() => '');
    this.stripeProductImageRequests.set(normalizedStripeProductId, request);
    return request;
  }

  private resolveProductImageUrl(product: ManageWellspaceProduct): string | null {
    const metadata = product?.metadata || {};
    return this.getImageUrlCandidates([
      metadata?.payrollAdminImageUrl,
      metadata?.payrollAdminLogoUrl,
      product?.imageUrl,
      product?.logoUrl,
      product?.iconUrl,
      product?.avatarUrl,
      product?.lightLogoUrl,
      product?.horizontalLogoUrl,
      metadata?.imageUrl,
      metadata?.logoUrl,
      metadata?.iconUrl,
      metadata?.avatarUrl,
      metadata?.lightLogoUrl,
      metadata?.horizontalLogoUrl,
      metadata?.image?.url,
      metadata?.logo?.url,
      metadata?.icon?.url,
    ])[0] || null;
  }

  private getImageUrlCandidates(values: any): string[] {
    const rawValues = Array.isArray(values) ? values : [values];
    const candidates: string[] = [];
    rawValues.forEach((value: any) => {
      const nestedValues = Array.isArray(value) ? value : [value];
      nestedValues.forEach((nestedValue: any) => {
        const normalizedValue = `${nestedValue || ''}`.trim();
        if (normalizedValue) {
          candidates.push(normalizedValue);
        }
      });
    });
    return candidates;
  }

  private getTrimmedText(value: any): string {
    return `${value ?? ''}`.trim();
  }
}
